import { useSeo } from "../lib/seo";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { loginHref, parseRole } from "../data/auth";
import { requestPasswordReset } from "../lib/auth";
import { toast } from "../lib/toast";

import AuthLayout, { AuthHeading } from "../components/auth/AuthLayout";
import { FormAlert, TextField } from "../components/ui/FormField";
import { emailRules } from "../components/auth/rules";
import { useT } from "../i18n";
import Button from "../components/ui/Button";

export default function ForgotPassword() {
  const t = useT();
  useSeo({ title: t.seo.forgotPassword, noindex: true }); // account pages stay out of search results
  const [searchParams] = useSearchParams();
  const role = parseRole(searchParams.get("role"));

  const [sentTo, setSentTo] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<{ email: string }>({ defaultValues: { email: "" } });

  const onSubmit = handleSubmit(async ({ email }) => {
    setSubmitError(null);
    try {
      await requestPasswordReset({ email, role });
      setSentTo(email);
    } catch (err) {
      const message = err instanceof Error ? err.message : t.common.somethingWrong;
      setSubmitError(message);
      toast.error(message);
    }
  });

  return (
    <AuthLayout>
      <Link
        to={loginHref(role)}
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-muted no-underline hover:text-ink"
      >
        <ArrowLeft size={14} />
        {t.common.login}
      </Link>

      <AuthHeading
        title={t.auth.forgot.title}
        subtitle={t.auth.roles[role].resetSubtitle}
      />

      {sentTo ? (
        <div className="grid gap-4">
          <FormAlert tone="success" title={t.auth.forgot.checkInbox}>
            {t.auth.forgot.sent(sentTo)}
          </FormAlert>
          <button
            type="button"
            onClick={() => setSentTo(null)}
            className="cursor-pointer border-0 bg-transparent p-0 text-left text-[13px] text-brand hover:text-brand-link"
          >
            {t.auth.forgot.differentEmail}
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="grid gap-3.5">
          <TextField
            label={t.common.email}
            type="email"
            autoComplete="email"
            placeholder={t.common.emailPlaceholder}
            error={errors.email?.message}
            {...register("email", emailRules(t))}
          />

          {submitError && <FormAlert>{submitError}</FormAlert>}

          <Button
            type="submit"
            disabled={isSubmitting}
            className="mt-1.5 h-11 w-full cursor-pointer border-0"
          >
            {isSubmitting ? t.auth.forgot.sending : t.auth.forgot.submit}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
